
export type CabinKey = "Economy" | "Premium" | "Business" | "First";

export interface CabinSetting {
  Checked: boolean;
  preSelect: boolean;
}

export interface Theme {
  textColor: string;
  bgColor: string;
}

export interface SmartCalendar {
  mode: "None" | "Basic" | "Advanced";
  dates?: string[];
  flexible?: boolean;
  includeReturn?: boolean;
}

export interface PassengerPref {
  PassengerGender: "Unselected" | "Male" | "Female" | string;
  PassengerDOB: "No" | string; 
}

export interface OtherSettingsText {
  MenuPosition: "L" | "R";
  ShowFareHeader: "On" | "Off";
  ShowSmartCalender: SmartCalendar; 
  Theme: Theme;
  CabinClass: Record<CabinKey, CabinSetting>;
  Passenger: PassengerPref;
  ShowHotelTab: "All" | "None" | string;
  Container: { Container: string };
  ShowCabinContainer: "None" | string;
}

// ---------------- DEFAULTS ----------------
export const DefaultOtherSettings: OtherSettingsText = {
  MenuPosition: "L",
  ShowFareHeader: "Off",
  ShowSmartCalender: {
    mode: "None",
    dates: [],
    flexible: false,
    includeReturn: false,
  },
  Theme: {
    textColor: "#ffffff",
    bgColor: "#242424",
  },
  CabinClass: {
    Economy:  { Checked: true,  preSelect: true  },
    Premium:  { Checked: true,  preSelect: false },
    Business: { Checked: true,  preSelect: false },
    First:    { Checked: true,  preSelect: false },
  },
  Passenger: {
    PassengerGender: "Unselected",
    PassengerDOB: "No",
  },
  ShowHotelTab: "All",
  Container: { Container: "#BRANCHCHANGE#" },
  ShowCabinContainer: "None",
};

// --------------- HELPERS ---------------
function isJsonLike(s: string): boolean {
  const t = s.trim();
  return (t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"));
}
function safeParse<T>(s: string): T | undefined {
  try { return JSON.parse(s) as T; } catch { return undefined; }
}
// shallow + nested object merge (arrays replace)
function deepMerge<T>(base: T, patch: Partial<T>): T {
  if (typeof base !== "object" || base === null) return (patch as T) ?? base;
  const out: any = Array.isArray(base) ? [...(base as any)] : { ...(base as any) };
  for (const [k, v] of Object.entries(patch as any)) {
    if (v && typeof v === "object" && !Array.isArray(v) && typeof out[k] === "object" && out[k] !== null && !Array.isArray(out[k])) {
      out[k] = deepMerge(out[k], v);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

// --- Legacy key=value (and weird /n) tolerant parser -> Partial<OtherSettingsText>
function parseLegacy(raw: string): Partial<OtherSettingsText> {
  // normalize '/n' to '\n'
  let s = raw.replace(/\/n/g, "\n");

  // split known markers
  const markers = [
    "menuPosition=", "showFareHeader=", "showSmartCalender=",
    "showHotelTab=", "showCabinContainer=", "{\"Economy\":", "{\"PassengerGender\":",
    "{\"textColor\":", "{\"Container\":",
  ];
  for (const m of markers) s = s.replace(new RegExp(`(?!^)(${m})`, "g"), "\n$1");

  const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const out: Partial<OtherSettingsText> = {};

  const tryJson = <T>(line: string) => safeParse<T>(line);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const kv = line.match(/^([A-Za-z][A-Za-z0-9_]*)=(.*)$/);
    if (kv) {
      const key = kv[1];
      const val = kv[2];

      switch (key) {
        case "menuPosition":
          if (val === "L" || val === "R") out.MenuPosition = val;
          break;
        case "showFareHeader":
          if (val === "On" || val === "Off") out.ShowFareHeader = val;
          break;
        case "showSmartCalender": {
          const mode = (["None","Basic","Advanced"].includes(val) ? (val as SmartCalendar["mode"]) : "None");
          let sc: SmartCalendar = { mode };
          const next = lines[i + 1];
          if (next && isJsonLike(next)) {
            const j = tryJson<Partial<SmartCalendar>>(next);
            if (j) { sc = { ...sc, ...j }; i++; }
          }
          out.ShowSmartCalender = sc;
          break;
        }
        case "showHotelTab":
          out.ShowHotelTab = (val as OtherSettingsText["ShowHotelTab"]) || "All";
          break;
        case "showCabinContainer":
          out.ShowCabinContainer = (val as OtherSettingsText["ShowCabinContainer"]) || "None";
          break;
        default:
          break;
      }
      continue;
    }

    // JSON blobs
    if (line.startsWith("{") && line.endsWith("}")) {
      if (line.includes("\"Economy\"") && line.includes("\"Checked\"")) {
        const j = tryJson<Record<CabinKey, CabinSetting>>(line);
        if (j) out.CabinClass = j;
        continue;
      }
      if (line.includes("\"PassengerGender\"")) {
        const j = tryJson<PassengerPref>(line);
        if (j) out.Passenger = j;
        continue;
      }
      if (line.includes("\"textColor\"") && line.includes("\"bgColor\"")) {
        const j = tryJson<Theme>(line);
        if (j) out.Theme = j;
        continue;
      }
      if (line.includes("\"Container\"")) {
        const j = tryJson<{ Container: string }>(line);
        if (j) out.Container = j;
        continue;
      }
    }
  }

  return out;
}


export function loadOtherSettings(raw?: string | null): OtherSettingsText {
  if (!raw || !raw.trim()) return DefaultOtherSettings;

  // Prefer pure JSON payloads
  if (isJsonLike(raw)) {
    const parsed = safeParse<Partial<OtherSettingsText>>(raw);
    if (parsed) return deepMerge(DefaultOtherSettings, parsed);
  }

  // fall back to legacy tolerant parsing
  const legacy = parseLegacy(raw);
  return deepMerge(DefaultOtherSettings, legacy);
}

export function stringifyOtherSettings(s: OtherSettingsText): string {
  return JSON.stringify(s);
}
